/**
 * Approvals: who has signed up and is waiting to be let in, with Approve and
 * Decline for each (migration 0012). The admin website's one page. It is no
 * part of the student portal, and the API refuses anyone but an admin on this
 * site's own origin whatever the page does.
 *
 * Deliberately plain: a list, the facts the person gave at signup, two
 * buttons. Each answer is sent at once and the row leaves the list when the
 * API confirms it; an answer somebody else gave first is reported and the
 * list is read again.
 */

import { useEffect, useState } from "preact/hooks";
import type { PendingStudent } from "@ls/shared";
import { ApiUnavailable, http } from "../../web/src/api";
import {
  adminErrorContract,
  decideAccessResponseContract,
  pendingListResponseContract,
} from "../../web/src/contracts.generated";
import { yearLabel } from "../../web/src/shell/context";
import { Card, EmptyState, PageHeader } from "../../web/src/ui";

type Load =
  | { readonly kind: "loading" }
  | { readonly kind: "ready"; readonly pending: readonly PendingStudent[] }
  | { readonly kind: "failed"; readonly message: string };

function when(iso: string): string {
  return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

export function Approvals() {
  const [load, setLoad] = useState<Load>({ kind: "loading" });
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const read = async () => {
    try {
      const reply = await http.get("/admin/access");
      const parsed = pendingListResponseContract.safeParse(reply.body);
      if (reply.status === 200 && parsed.success) {
        setLoad({ kind: "ready", pending: parsed.data.pending });
        return;
      }
      const problem = adminErrorContract.safeParse(reply.body);
      setLoad({ kind: "failed", message: problem.success ? problem.data.message : "The list could not be read." });
    } catch (error) {
      setLoad({ kind: "failed", message: error instanceof ApiUnavailable ? "The server could not be reached." : "The list could not be read." });
    }
  };

  useEffect(() => {
    void read();
  }, []);

  const decide = async (person: PendingStudent, decision: "approve" | "decline") => {
    setBusy(person.id);
    setNotice(null);
    try {
      const reply = await http.post(`/admin/access/${encodeURIComponent(person.id)}`, { decision });
      const done = decideAccessResponseContract.safeParse(reply.body);
      const who = person.name ?? person.email;
      if (reply.status === 200 && done.success) {
        setNotice(done.data.access === "approved" ? `${who} is approved, and has been emailed.` : `${who} was declined, and has been emailed.`);
        setLoad((current) =>
          current.kind === "ready" ? { kind: "ready", pending: current.pending.filter((p) => p.id !== person.id) } : current,
        );
        return;
      }
      const problem = adminErrorContract.safeParse(reply.body);
      setNotice(problem.success && problem.data.error === "not_pending" ? `${who} was already answered.` : "That did not go through. Try again.");
      await read();
    } catch {
      setNotice("The server could not be reached. Try again.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div class="approvals">
      <PageHeader eyebrow="Accounts" title="Approvals" />
      {notice ? (
        <p class="notice approvals-notice" role="status">
          {notice}
        </p>
      ) : null}
      {load.kind === "loading" ? <p class="note">Loading...</p> : null}
      {load.kind === "failed" ? (
        <p class="notice bad" role="alert">
          {load.message}
        </p>
      ) : null}
      {load.kind === "ready" && load.pending.length === 0 ? (
        <Card>
          <EmptyState title="Nobody is waiting." line="Every account opens at once since 28 September 2026; one made before then that still waits appears here." />
        </Card>
      ) : null}
      {load.kind === "ready" && load.pending.length > 0 ? (
        <ul class="card bare list approvals-list">
          {load.pending.map((person) => (
            <li key={person.id} class="approvals-row" data-student={person.id}>
              <div class="approvals-who">
                <b>{person.name ?? "No name given"}</b>
                <span>{person.email}</span>
                <span class="note">{[person.college, yearLabel(person.year), person.city].filter(Boolean).join(", ")}</span>
                <span class="note">Signed up {when(person.createdAt)}</span>
              </div>
              <div class="approvals-actions">
                <button
                  type="button"
                  class="btn primary sm"
                  data-decision="approve"
                  disabled={busy !== null}
                  onClick={() => void decide(person, "approve")}
                >
                  Approve
                </button>
                <button
                  type="button"
                  class="btn sm"
                  data-decision="decline"
                  disabled={busy !== null}
                  onClick={() => void decide(person, "decline")}
                >
                  Decline
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
